import {Box, Sheet, SheetProps, Stack, Theme} from "@mui/joy";
import React, {ReactNode, useRef} from "react";
import {BaseTimelineScene} from "./timeline.tsx";
import {SceneViewProps} from "./sceneViewProps.tsx";
import {format} from "date-fns";
import {mergeSx} from "../components/joyx.tsx";
import {useVirtualizer} from "@tanstack/react-virtual";
import {TimelineTimePoint, TimelineTimeSpan} from "./timelineTimeCoordinate.tsx";

function TimelineCell({children, ...rest}: SheetProps) {
    return <Sheet {...rest}>
        <Stack direction="column" sx={{alignItems: "center", height: "100%", justifyContent: "stretch"}}>
            <Box sx={{flex: 1}}/>
            <Stack direction="row" spacing={1.6} sx={{
                width: "100%", fontSize: "md", alignItems: "center",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
                {children}
            </Stack>
            <Box sx={{flex: 1}}/>
        </Stack>
    </Sheet>
}


export function DiarySceneView<SceneT extends BaseTimelineScene>(props: SceneViewProps<SceneT>) {
    const {
        ref,
        scene,
        color: inColor,
        variant: inVariant,
        sx: inSx,
        children,
        dataIndex,
        ...rest
    } = props;
    const color = inColor ?? "neutral";
    const variant = inVariant ?? "soft";

    const dateFormat = "yyyy.MM.dd";
    const timeFormat = "HH:mm";
    const unk = "??:??";

    const facear: React.JSX.Element[] = [];
    const atWhenZrv = TimelineTimePoint.safeParse(scene.when);
    if (atWhenZrv.success) {
        const at = atWhenZrv.data.at;
        facear.push(<Box key="date">{format(at, dateFormat)}</Box>);
        facear.push(<Box key="time">{format(at, timeFormat)}</Box>);
    } else {
        const spanWhenZrv = TimelineTimeSpan.safeParse(scene.when);
        if (spanWhenZrv.success) {
            const span = spanWhenZrv.data.span;
            if (span.valid) {
                facear.push(<Box key="date">{format(span.lower ?? span.upper!, dateFormat)}</Box>);
                facear.push(<Box key="time">{(span.lower ? format(span.lower, timeFormat) : unk) + " - " +
                    (span.upper ? format(span.upper, timeFormat) : unk)}</Box>);
            }
        }
    }
    if (facear.length === 0) {
        facear.push(<Box key="unk">{unk}</Box>);
    }

    const whenSx = mergeSx({
        width: "12rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "16px",
        fontSize: "lg",
    }, inSx);

    const whatSx = mergeSx({
        px: 2, flex: 1,
        display: "flex", flexDirection: "column", justifyContent: "center",
        alignItems: "left",
        borderRadius: "16px",
    }, inSx);

    return <Box ref={ref} data-index={dataIndex} sx={{
        py: 0.8,
        display: "flex",
        flexDirection: "row",
        columnGap: 3,
        alignItems: "center",
        width: "100%",
    }}>
        <Stack direction="row" spacing={1.8} px={{width: "100%"}}>
            <Sheet variant={variant} color={color} sx={whenSx} {...rest}>
                <Stack direction="row" sx={{justifyContent: "center", alignItems: "center", width: "100%"}}>
                    <Stack direction="column" spacing={0.2}
                           sx={(theme: Theme) => ({
                               px: "1rem", py: "0.6rem", alignItems: "center",
                               fontSize: theme.fontSize.lg,
                           })}>
                        {[...facear]}
                    </Stack>
                </Stack>
            </Sheet>
            <TimelineCell color={color} variant={variant} sx={whatSx} {...rest}>
                {children}
            </TimelineCell>
        </Stack>
    </Box>
}

export interface DiaryViewProps<SceneT extends BaseTimelineScene> {
    sortedScenes: SceneT[];
    Entry: (props: SceneViewProps<SceneT>) => ReactNode;
    targetLabel?: ReactNode;
    // onStampInput?: (stampFood: GenericFlavorlessStampSpawnFood) => Promise<unknown>;
}

const estimatedTimelineRowHeight = 120;

export function DiaryView<SceneT extends BaseTimelineScene>(props: DiaryViewProps<SceneT>) {
    const {
        sortedScenes,
        Entry,
        //      targetLabel,
    } = props;

    //  const theme = useTheme();
    // const {scrollToBottom} = useContext(ScrollableViewContext);
    // const prevSceneProps = usePrevious(sortedSceneProps);
    const numScenes = sortedScenes.length;
    // on first run, usePrevious returns null or undefined. we have to
    // guard against that
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    // const numPrevScenes = prevSceneProps?.length ?? 0;
    // const hasGrown = numPrevScenes > 0 && numScenes > numPrevScenes;
    //
    // useEffect(() => {
    //     if (hasGrown) {
    //         setTimeout(() => {
    //             scrollToBottom();
    //         }, 0);
    //     }
    // }, [hasGrown, scrollToBottom]);

    const hostRef = useRef<HTMLDivElement>(null);
    const virtualizer = useVirtualizer({
        count: numScenes,
        getScrollElement: () => hostRef.current,
        estimateSize: () => estimatedTimelineRowHeight,
    });
    const vitems = virtualizer.getVirtualItems();

    return <Box ref={hostRef}>
        <Box sx={{
            height: virtualizer.getTotalSize(),
            width: "100%",
            position: "relative",
        }}>
            <Box sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${(vitems[0]?.start ?? 0).toString()}px)`,
            }}>
                {vitems.map(vitem => {
                    const ix = vitem.index;
                    const scene = sortedScenes[ix];
                    return scene
                        ? <Entry key={vitem.key} dataIndex={vitem.index}
                                 ref={virtualizer.measureElement}
                                 scene={scene}/>
                        : null;
                })}
            </Box>
        </Box>
    </Box>
}